import { type SelectProps } from '@jouzen/ecom-components/v3/Select';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { useCountriesList } from '@/queries/Countries';

export interface CountryData {
  countryOptions: SelectProps['options'];
  hasRegions: boolean;
  isError: boolean;
  isLoading: boolean;
  regionOptions: SelectProps['options'];
}

const useCountryData = (countryCode: string): CountryData => {
  const t = useTranslations();

  // fetch list of all countries to show
  const { data: countriesList = [], isError, isLoading } = useCountriesList();
  const countryDetails = countriesList.find(
    (country) => country.countryCode === countryCode,
  );

  const countryOptions = useMemo(
    () =>
      countriesList
        .map((country) => ({
          label: country.labelMsgKey
            ? t(country.labelMsgKey)
            : (country.name ?? ''),
          value: country.countryCode ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countriesList, t],
  );

  const hasRegions: boolean = useMemo(
    () => Boolean(countryDetails?.regions?.length),
    [countryDetails?.regions?.length],
  );

  const regionOptions =
    useMemo(
      () =>
        countryDetails?.regions
          ?.map((region) => ({
            label: region?.labelMsgKey
              ? t(region.labelMsgKey)
              : (region?.label ?? ''),
            value: region?.value ?? '',
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      [countryDetails?.regions, t],
    ) ?? [];

  return {
    countryOptions,
    hasRegions,
    isError,
    isLoading: !countriesList.length && isLoading,
    regionOptions,
  };
};

export default useCountryData;
